import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  LedgerSafeTraditionReaderView,
  type LedgerSafeTraditionReaderViewProps,
} from './LedgerSafeTraditionReaderView'
import {
  createTextStructureLayer,
  getTextStructureLayerId,
} from '../../lib/readerTextStructure'
import {
  collectUntrackedReaderAnalyticalActivities,
  resolveReaderAnalyticalMutation,
  type ReaderAnalyticalActivity,
} from '../../lib/readerAnalyticalTasks'
import { useReaderTextStructureStore } from '../../store/readerTextStructureStore'
import { useAnnotationResearchNoteStore } from '../../store/annotationResearchNoteStore'
import { useAnalyticalTaskStore } from '../../store/analyticalTaskStore'
import type { TextStructureCommand } from '../../types/readerTextStructureTypes'
import type { AnalyticalTaskSet, CharacterIndexTaskSetEnrollment } from '../../types/analyticalTaskTypes'

interface LedgerSafeTraditionReaderWithStructureProps extends LedgerSafeTraditionReaderViewProps {
  readOnly?: boolean
}

export const LedgerSafeTraditionReaderWithStructure: React.FC<LedgerSafeTraditionReaderWithStructureProps> = ({
  readOnly = false,
  ...readerProps
}) => {
  const {
    onAdjudicateCharacterEvidence,
    onDeleteCharacter,
    onRenameCharacter,
  } = readerProps
  const chapterId = readerProps.rows.find(row => row.chapterId)?.chapterId || ''
  const layerId = chapterId
    ? getTextStructureLayerId(chapterId, readerProps.sourceWitnessId)
    : ''
  const persistedLayer = useReaderTextStructureStore(state => (
    layerId ? state.layers.get(layerId) : undefined
  ))
  const loadLayer = useReaderTextStructureStore(state => state.loadLayer)
  const applyCommand = useReaderTextStructureStore(state => state.applyCommand)
  const persistedResearchNotes = useAnnotationResearchNoteStore(state => (
    chapterId ? state.notesByScope.get(`${chapterId}::${readerProps.sourceWitnessId}`) || [] : []
  ))
  const loadResearchNotes = useAnnotationResearchNoteStore(state => state.loadScope)
  const saveResearchNote = useAnnotationResearchNoteStore(state => state.saveNote)
  const archiveResearchNote = useAnnotationResearchNoteStore(state => state.archiveNote)
  const archiveResearchNotesForTarget = useAnnotationResearchNoteStore(state => state.archiveTarget)
  const analyticalTaskSets = useAnalyticalTaskStore(state => state.taskSets)
  const loadAnalyticalTaskSets = useAnalyticalTaskStore(state => state.loadForWitness)
  const ensureCharacterIndexTaskSet = useAnalyticalTaskStore(state => state.ensureCharacterIndexTaskSet)
  const recordCharacterActivity = useAnalyticalTaskStore(state => state.recordCharacterActivity)
  const createCheckpoint = useAnalyticalTaskStore(state => state.createCheckpoint)
  const setWorkstreamState = useAnalyticalTaskStore(state => state.setWorkstreamState)
  const setCheckpointState = useAnalyticalTaskStore(state => state.setCheckpointState)
  const mergeCharacterSubjects = useAnalyticalTaskStore(state => state.mergeCharacterSubjects)
  const archiveCharacterSubject = useAnalyticalTaskStore(state => state.archiveCharacterSubject)
  const renameCharacterSubject = useAnalyticalTaskStore(state => state.renameCharacterSubject)
  const fallbackLayer = useMemo(() => (
    chapterId
      ? createTextStructureLayer({ chapterId, sourceWitnessId: readerProps.sourceWitnessId })
      : undefined
  ), [chapterId, readerProps.sourceWitnessId])
  const rowOrderById = useMemo(() => new Map(
    [...readerProps.rows]
      .sort((left, right) => left.position - right.position || left.labelNumber - right.labelNumber)
      .map((row, index) => [row.id, index]),
  ), [readerProps.rows])
  const rowLabelsById = useMemo(() => new Map(readerProps.rows.map(row => [
    row.id,
    `${row.labelPrefix}-${String(row.labelNumber).padStart(3, '0')}`,
  ])), [readerProps.rows])
  const activeWitness = useMemo(() => readerProps.witnesses.find(
    witness => witness.id === readerProps.sourceWitnessId,
  ), [readerProps.sourceWitnessId, readerProps.witnesses])
  const persistedAnalyticalTaskSet = useMemo(() => Array.from(analyticalTaskSets.values())
    .filter(taskSet => (
      taskSet.productKind === 'character-index' &&
      taskSet.enrollments.some(enrollment => (
        enrollment.witnessId === readerProps.sourceWitnessId &&
        (!chapterId || (enrollment.operationalChapterId || enrollment.chapterId) === chapterId) &&
        (!readerProps.characterLayer || (
          enrollment.operationalWorkId || enrollment.workId
        ) === readerProps.characterLayer.book_id)
      ))
    ))
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0], [
    analyticalTaskSets,
    chapterId,
    readerProps.characterLayer,
    readerProps.sourceWitnessId,
  ])
  const activeAnalyticalTaskSet = readerProps.analyticalTaskSet || persistedAnalyticalTaskSet
  const reconciliationRunningRef = useRef(false)

  useEffect(() => {
    if (!chapterId || readerProps.textStructureLayer) {
      return
    }
    void loadLayer(chapterId, readerProps.sourceWitnessId)
  }, [chapterId, loadLayer, readerProps.sourceWitnessId, readerProps.textStructureLayer])

  useEffect(() => {
    if (!chapterId || readerProps.annotationResearchNotes) {
      return
    }
    void loadResearchNotes(chapterId, readerProps.sourceWitnessId)
  }, [
    chapterId,
    loadResearchNotes,
    readerProps.annotationResearchNotes,
    readerProps.sourceWitnessId,
  ])

  useEffect(() => {
    if (!readerProps.analyticalTaskSet) {
      void loadAnalyticalTaskSets(readerProps.sourceWitnessId)
    }
  }, [loadAnalyticalTaskSets, readerProps.analyticalTaskSet, readerProps.sourceWitnessId])

  const editPersistedStructure = useCallback(async (command: TextStructureCommand) => {
    if (!chapterId || readOnly) {
      return
    }
    await applyCommand({
      chapterId,
      sourceWitnessId: readerProps.sourceWitnessId,
      command,
      rowOrderById,
    })
  }, [applyCommand, chapterId, readOnly, readerProps.sourceWitnessId, rowOrderById])

  const ensureReaderTaskSet = useCallback(async (): Promise<AnalyticalTaskSet | null> => {
    if (activeAnalyticalTaskSet) return activeAnalyticalTaskSet
    if (!chapterId || !activeWitness || !readerProps.characterLayer) return null
    const loaded = await loadAnalyticalTaskSets(readerProps.sourceWitnessId)
    const loadedForContext = loaded
      .filter(taskSet => (
        taskSet.productKind === 'character-index' &&
        taskSet.enrollments.some(enrollment => (
          enrollment.witnessId === readerProps.sourceWitnessId &&
          (enrollment.operationalChapterId || enrollment.chapterId) === chapterId &&
          (enrollment.operationalWorkId || enrollment.workId) === readerProps.characterLayer!.book_id
        ))
      ))
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0]
    if (loadedForContext) return loadedForContext
    const workLabel = readerProps.contextLabel?.trim() || readerProps.characterLayer.book_id
    const enrollment: CharacterIndexTaskSetEnrollment = {
      workId: readerProps.characterLayer.book_id,
      workLabel,
      chapterId,
      witnessId: activeWitness.id,
      witnessLabel: activeWitness.sigla || activeWitness.description || activeWitness.id,
      role: 'primary',
      readiness: {
        sourceObject: activeWitness.source_object_id ? 'ready' : 'missing',
        transcription: readerProps.rows.length > 0 ? 'ready' : 'missing',
        collation: 'skipped',
      },
      collationPolicy: 'skipped',
    }
    return ensureCharacterIndexTaskSet({
      title: `${workLabel} Character Index`,
      enrollment,
    })
  }, [
    activeAnalyticalTaskSet,
    activeWitness,
    chapterId,
    ensureCharacterIndexTaskSet,
    loadAnalyticalTaskSets,
    readerProps.characterLayer,
    readerProps.contextLabel,
    readerProps.rows.length,
    readerProps.sourceWitnessId,
  ])

  const findActiveCheckpointId = useCallback((
    taskSet: AnalyticalTaskSet,
    characterId: string,
  ): string | undefined => taskSet.checkpoints
    .filter(checkpoint => (
      checkpoint.state === 'in_progress' &&
      checkpoint.scope.witnessId === readerProps.sourceWitnessId &&
      checkpoint.subjectIds.includes(`character:${characterId}`)
    ))
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0]?.id, [
    readerProps.sourceWitnessId,
  ])

  const recordActivities = useCallback(async (
    initialTaskSet: AnalyticalTaskSet,
    activities: ReaderAnalyticalActivity[],
    sourceLayer: NonNullable<LedgerSafeTraditionReaderViewProps['characterLayer']>,
  ): Promise<AnalyticalTaskSet> => {
    let taskSet = initialTaskSet
    for (const activity of activities) {
      const character = sourceLayer.characters.find(candidate => candidate.character_id === activity.characterId)
      const entry = readerProps.characterIndexEntries?.find(candidate => candidate.id === activity.characterId)
      const label = character?.display_name || entry?.names.primary || activity.characterId
      const activeCheckpointId = findActiveCheckpointId(taskSet, activity.characterId)
      const result: Awaited<ReturnType<typeof recordCharacterActivity>> = await recordCharacterActivity({
        taskSetId: taskSet.id,
        character: { id: activity.characterId, label, metadata: character?.metadata },
        chapterId,
        witnessId: readerProps.sourceWitnessId,
        dimension: activity.dimension,
        position: {
          ...activity.position,
          unitLabel: rowLabelsById.get(activity.position.rowId),
        },
        activeCheckpointId,
      })
      taskSet = result.taskSet
    }
    return taskSet
  }, [
    chapterId,
    findActiveCheckpointId,
    readerProps.characterIndexEntries,
    readerProps.sourceWitnessId,
    recordCharacterActivity,
    rowLabelsById,
  ])

  const adjudicateWithAnalyticalTracking = useCallback(async (
    command: Parameters<NonNullable<LedgerSafeTraditionReaderViewProps['onAdjudicateCharacterEvidence']>>[0],
  ) => {
    if (!onAdjudicateCharacterEvidence) return
    const sourceLayer = readerProps.characterLayer
    await onAdjudicateCharacterEvidence(command)
    if (readOnly || !sourceLayer) return
    const mutation = resolveReaderAnalyticalMutation(command, sourceLayer)
    if (mutation.activities.length === 0 && !mutation.merge) return
    const ensuredTaskSet = await ensureReaderTaskSet()
    if (!ensuredTaskSet) return
    let taskSet = await recordActivities(ensuredTaskSet, mutation.activities, sourceLayer)
    if (mutation.merge) {
      const sourceSubjectId = `character:${mutation.merge.sourceCharacterId}`
      const receivingSubjectId = `character:${mutation.merge.receivingCharacterId}`
      if (
        taskSet.subjects.some(subject => subject.id === sourceSubjectId && !subject.archivedAt) &&
        taskSet.subjects.some(subject => subject.id === receivingSubjectId && !subject.archivedAt)
      ) {
        await mergeCharacterSubjects({
          taskSetId: taskSet.id,
          sourceCharacterId: mutation.merge.sourceCharacterId,
          receivingCharacterId: mutation.merge.receivingCharacterId,
        })
      }
    }
  }, [
    ensureReaderTaskSet,
    mergeCharacterSubjects,
    onAdjudicateCharacterEvidence,
    readOnly,
    readerProps.characterLayer,
    recordActivities,
  ])

  useEffect(() => {
    const sourceLayer = readerProps.characterLayer
    if (readOnly || !sourceLayer || sourceLayer.occurrences.length === 0 || reconciliationRunningRef.current) {
      return
    }
    reconciliationRunningRef.current = true
    void (async () => {
      try {
        const taskSet = await ensureReaderTaskSet()
        if (!taskSet) return
        const activities = collectUntrackedReaderAnalyticalActivities(
          sourceLayer,
          taskSet,
          readerProps.sourceWitnessId,
        )
        if (activities.length > 0) {
          await recordActivities(taskSet, activities, sourceLayer)
        }
      } finally {
        reconciliationRunningRef.current = false
      }
    })()
  }, [
    activeAnalyticalTaskSet?.updatedAt,
    ensureReaderTaskSet,
    readOnly,
    readerProps.characterLayer,
    readerProps.sourceWitnessId,
    recordActivities,
  ])

  const renameCharacterWithAnalyticalTracking = useCallback(async (
    characterId: string,
    displayName: string,
    kind?: Parameters<NonNullable<LedgerSafeTraditionReaderViewProps['onRenameCharacter']>>[2],
  ) => {
    if (!onRenameCharacter) return
    await onRenameCharacter(characterId, displayName, kind)
    const taskSet = activeAnalyticalTaskSet
    if (!readOnly && taskSet?.subjects.some(subject => subject.id === `character:${characterId}`)) {
      await renameCharacterSubject({ taskSetId: taskSet.id, characterId, label: displayName })
    }
  }, [activeAnalyticalTaskSet, onRenameCharacter, readOnly, renameCharacterSubject])

  const deleteCharacterWithAnalyticalTracking = useCallback(async (characterId: string) => {
    if (!onDeleteCharacter) return
    await onDeleteCharacter(characterId)
    const taskSet = activeAnalyticalTaskSet
    if (!readOnly && taskSet?.subjects.some(subject => subject.id === `character:${characterId}`)) {
      await archiveCharacterSubject({ taskSetId: taskSet.id, characterId })
    }
  }, [activeAnalyticalTaskSet, archiveCharacterSubject, onDeleteCharacter, readOnly])

  return (
    <LedgerSafeTraditionReaderView
      {...readerProps}
      analyticalTaskSet={activeAnalyticalTaskSet}
      textStructureLayer={readerProps.textStructureLayer || persistedLayer || fallbackLayer}
      onEditTextStructure={readerProps.onEditTextStructure || (readOnly ? undefined : editPersistedStructure)}
      annotationResearchNotes={readerProps.annotationResearchNotes || persistedResearchNotes}
      onSaveAnnotationResearchNote={readerProps.onSaveAnnotationResearchNote || (
        readOnly ? undefined : saveResearchNote
      )}
      onArchiveAnnotationResearchNote={readerProps.onArchiveAnnotationResearchNote || (
        readOnly ? undefined : archiveResearchNote
      )}
      onArchiveAnnotationResearchNotesForTarget={readerProps.onArchiveAnnotationResearchNotesForTarget || (
        readOnly ? undefined : archiveResearchNotesForTarget
      )}
      onAdjudicateCharacterEvidence={readerProps.onAdjudicateCharacterEvidence
        ? adjudicateWithAnalyticalTracking
        : undefined}
      onRenameCharacter={readerProps.onRenameCharacter
        ? renameCharacterWithAnalyticalTracking
        : undefined}
      onDeleteCharacter={readerProps.onDeleteCharacter
        ? deleteCharacterWithAnalyticalTracking
        : undefined}
      onCreateAnalyticalCheckpoint={readerProps.onCreateAnalyticalCheckpoint || (
        readOnly || !activeAnalyticalTaskSet
          ? undefined
          : async request => {
              await createCheckpoint({ taskSetId: activeAnalyticalTaskSet.id, ...request })
            }
      )}
      onSetAnalyticalWorkstreamState={readerProps.onSetAnalyticalWorkstreamState || (
        readOnly || !activeAnalyticalTaskSet
          ? undefined
          : async (workstreamId, state) => {
              await setWorkstreamState({
                taskSetId: activeAnalyticalTaskSet.id,
                workstreamId,
                state,
              })
            }
      )}
      onSetAnalyticalCheckpointState={readerProps.onSetAnalyticalCheckpointState || (
        readOnly || !activeAnalyticalTaskSet
          ? undefined
          : async (checkpointId, state) => {
              await setCheckpointState({
                taskSetId: activeAnalyticalTaskSet.id,
                checkpointId,
                state,
              })
            }
      )}
    />
  )
}
